import { ChangeDetectionStrategy, Component, DestroyRef, Renderer2, TemplateRef, computed, effect, inject, input, output, signal } from '@angular/core';
import { DOCUMENT, NgTemplateOutlet } from '@angular/common';

export interface ModalConfig {
  title: string;
  width?: string;
  maxWidth?: string;
  height?: string;
  maxHeight?: string;
  showCloseButton?: boolean;
  closeOnBackdropClick?: boolean;
  showExpandButton?: boolean;
  expandedWidth?: string;
  expandedMaxWidth?: string;
  expandedHeight?: string;
  expandedMaxHeight?: string;
}

@Component({
  selector: 'app-generic-modal',
  templateUrl: './generic-modal.component.html',
  styleUrls: ['./generic-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgTemplateOutlet],
  host: {
    '(click)': 'onHostClick($event)',
    '(document:keydown)': 'onKeydown($event)'
  }
})
export class GenericModalComponent {
  readonly isOpen = input<boolean>(false);
  readonly config = input<ModalConfig>({
    title: 'Modal',
    width: '90vw',
    maxWidth: '600px',
    height: 'auto',
    maxHeight: '90vh',
    showCloseButton: true,
    closeOnBackdropClick: true,
    showExpandButton: false
  });
  readonly contentTemplate = input<TemplateRef<unknown>>();
  readonly data = input<unknown>();

  readonly closeModal = output<void>();
  readonly modalAction = output<{action: string, data?: unknown}>();
  readonly toggleExpanded = output<boolean>();

  readonly isExpanded = signal(false);

  private readonly renderer = inject(Renderer2);
  private readonly document = inject(DOCUMENT);
  private readonly destroyRef = inject(DestroyRef);

  readonly currentWidth = computed(() => {
    const cfg = this.config();
    return this.isExpanded() && cfg.expandedWidth ? cfg.expandedWidth : (cfg.width || '90vw');
  });

  readonly currentMaxWidth = computed(() => {
    const cfg = this.config();
    return this.isExpanded() && cfg.expandedMaxWidth ? cfg.expandedMaxWidth : (cfg.maxWidth || '600px');
  });

  readonly currentHeight = computed(() => {
    const cfg = this.config();
    return this.isExpanded() && cfg.expandedHeight ? cfg.expandedHeight : (cfg.height || 'auto');
  });

  readonly currentMaxHeight = computed(() => {
    const cfg = this.config();
    return this.isExpanded() && cfg.expandedMaxHeight ? cfg.expandedMaxHeight : (cfg.maxHeight || '90vh');
  });

  constructor() {
    effect(() => {
      if (this.isOpen()) {
        this.renderer.addClass(this.document.body, 'modal-open');
      } else {
        this.renderer.removeClass(this.document.body, 'modal-open');
      }
    });

    this.destroyRef.onDestroy(() => {
      this.renderer.removeClass(this.document.body, 'modal-open');
    });
  }

  onHostClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    if (target.classList.contains('modal-overlay') && this.config().closeOnBackdropClick !== false) {
      this.close();
    }
  }

  onBackdropClick() {
    if (this.config().closeOnBackdropClick !== false) {
      this.close();
    }
  }

  close() {
    this.closeModal.emit();
  }

  toggleModalSize() {
    this.isExpanded.update(v => !v);
    this.toggleExpanded.emit(this.isExpanded());
  }

  onKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Escape') {
      return;
    }

    if (this.isOpen()) {
      event.stopPropagation();
      this.close();
    }
  }

  emitAction(action: string, data?: unknown) {
    this.modalAction.emit({ action, data });
  }
}
